<div class="container mx-auto p-4 relative">
  <!-- All Marketplaces -->
  <div class="flex items-center mb-4">
    <a routerLink="/admin/marketplaces" class="text-momentum-deep_purple hover:text-momentum-royal_purple underline hover:no-underline mr-2"> All Marketplaces </a>
  </div>

  <!-- Marketplace Form -->
  @if (marketplaceForm) {
    <form [formGroup]="marketplaceForm" (ngSubmit)="saveMarketplace()">
      <!-- ---------------------------------- Marketplace Setup Section -------------------------------------------- -->
      <div class="flex items-center mb-4">
        <h2 class="text-xl text-indigo-950 font-semibold mr-2">Marketplace Setup</h2>
        <app-info-modal-button [data]="marketplaceSetupInfo"></app-info-modal-button>
      </div>
      <div class="bg-white rounded-lg shadow p-6 mb-6">
        <!-- API Type -->
        <div class="mb-6">
          <label class="block font-semibold text-agentium_gray-text mb-2">API Type <span class="text-red-800">*</span> </label>
          <div class="flex items-center mb-2">
            <div class="flex">
              <div class="mr-4">
                <input
                  type="radio"
                  id="instantMarketplace"
                  formControlName="marketplaceType"
                  value="{{ marketplaceTypeEnum.INSTANT }}"
                  class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 focus:ring-momentum-deep_purple"
                />
                <label for="instantMarketplace" class="ml-2 cursor-pointer text-agentium_gray-text">Instant</label>
              </div>
              <div>
                <input
                  type="radio"
                  id="durationMarketplace"
                  formControlName="marketplaceType"
                  value="{{ marketplaceTypeEnum.DURATION }}"
                  class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 focus:ring-momentum-deep_purple"
                />
                <label for="durationMarketplace" class="ml-2 cursor-pointer text-agentium_gray-text">Duration</label>
              </div>
            </div>
          </div>
        </div>

        <!-- Is Generic and Is Super Sub Marketplaces -->
        <div class="mb-6 grid grid-flow-col grid-rows-1 grid-cols-2 max-w-[480px]">
          <!-- Is Generic Marketplace -->
          <div class="flex items-center">
            <label class="block font-semibold text-agentium_gray-text mr-4">Is Generic Marketplace</label>
            <div class="flex items-center">
              <input type="checkbox" formControlName="generic" id="genericMarketplaceToggle" class="sr-only" />
              @let isGenericMarketplace = marketplaceForm.get("generic")?.value;
              <label
                for="genericMarketplaceToggle"
                class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                [ngClass]="{ 'bg-momentum-deep_purple': isGenericMarketplace, 'bg-gray-300': !isGenericMarketplace }"
              >
                <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': isGenericMarketplace }"></span>
              </label>
            </div>
          </div>
        </div>

        <!-- User ID, Agency ID and Info -->
        <div class="mb-6 flex flex-col">
          <div class="flex flex-col">
            <div class="flex items-center">
              <!-- Agency ID -->
              <div>
                <label for="agencyId" class="block font-semibold text-agentium_gray-text mb-2">Agency ID</label>
                <div class="flex items-center">
                  <input id="agencyId" type="number" name="agencyId" formControlName="agencyId" class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-[7rem]" />
                </div>
              </div>

              <!-- Is Premier Partner -->
              @if (!showAgencyIdSpinner && isAgencyFound) {
                <div class="flex items-center ml-4 mt-6">
                  <input
                    type="checkbox"
                    id="premierPartnerMarketplace"
                    formControlName="premierPartnerMarketplace"
                    class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                  />
                  <label for="premierPartnerMarketplace" class="ml-2 cursor-pointer font-semibold text-agentium_gray-text"> Is Premier Partner </label>
                </div>
              }
            </div>

            <!-- Agency Info Loading Spinner -->
            @if (showAgencyIdSpinner) {
              <div class="pl-2 pr-2 pt-4">
                <fa-icon [icon]="faGear" animation="spin" class="pl-4 text-slate-600" size="lg"></fa-icon>
              </div>
            }

            <!-- Agency Info -->
            @if (!showAgencyIdSpinner && isAgencyFound && agency) {
              <div class="pr-2 pt-3">
                <p class="text-agentium_gray-text">{{ agency.name }}</p>
                <p class="text-agentium_gray-text">{{ agency.address?.city }}, {{ agency.address?.regionCode }} {{ agency.address?.zipCode }}</p>
                <p class="text-agentium_gray-text">
                  {{ agency.contact?.name }}
                  @if (agency.contact?.email) {
                    <span> ({{ agency.contact?.email }}) </span>
                  }
                </p>
              </div>
            }
          </div>
          @if (showAgencyIdNotFoundError) {
            <div class="mt-2 text-red-500">No agency found matching the provided agency ID</div>
          }
        </div>

        <!-- Names and Phone Number -->
        <div class="mb-6 grid grid-flow-col grid-rows-1 grid-cols-3 gap-y-3 gap-x-6 max-w-[1000px]">
          <!-- Marketplace Name -->
          <div>
            <label for="marketplaceName" class="block font-semibold text-agentium_gray-text mb-2">Marketplace Name <span class="text-red-800">*</span> </label>
            <div class="flex items-center">
              <input
                id="marketplaceName"
                type="text"
                name="marketplaceName"
                formControlName="marketplaceName"
                class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-80"
              />
            </div>
            @if (showMarketplaceNameError) {
              <div class="text-red-500 mt-2">Please provide marketplace name</div>
            }
          </div>

          <!-- legal Entity Name -->
          <div>
            <label for="legalEntityName" class="block font-semibold text-agentium_gray-text mb-2">Legal Entity Name</label>
            <div class="flex items-center">
              <input id="legalEntityName" type="text" name="name" formControlName="legalEntityName" class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-80" />
            </div>
          </div>

          <!-- Legal Entity URL -->
          <div>
            <label for="legalEntityUrl" class="block font-semibold text-agentium_gray-text mb-2">Legal Entity URL</label>
            <div class="flex items-center">
              <input id="legalEntityUrl" type="url" name="legalEntityUrl" placeholder="https://example.com/partners" formControlName="legalEntityUrl" class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-80" />
            </div>
            <div class="text-agentium_gray-text text-sm mt-2">Optional. When set, the legal entity name is shown as a link in the second TCPA on the consumer funnels.</div>
            @if (marketplaceForm.get("legalEntityUrl")?.invalid && marketplaceForm.get("legalEntityUrl")?.dirty) {
              <div class="text-red-500 mt-2">Please provide a URL starting with http:// or https://</div>
            }
          </div>

          <!-- Phone Number -->
          <div>
            <label for="phoneNumber" class="block font-semibold text-agentium_gray-text mb-2">Phone Number</label>
            <div class="flex items-center">
              <input
                id="phoneNumber"
                type="tel"
                name="phone"
                placeholder="xxxyyyzzzz"
                formControlName="phoneNumber"
                class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-[8rem]"
              />
            </div>
            @if (showPhoneNumberError) {
              <div class="text-red-500 mt-2">Please provide a valid phone number</div>
            }
          </div>
        </div>

        <!-- Default Purchaser -->
        @if (!showAgencyIdSpinner && isAgencyFound) {
          <div class="mb-6 flex flex-col">
            <div class="flex flex-col">
              <div class="flex items-center">
                <!-- Default Purchasing Agent Email Address -->
                <div>
                  <label for="defaultPurchasingAgentEmailAddress" class="block font-semibold text-agentium_gray-text mb-2"
                    >Default Purchasing Agent Email Address <span class="text-red-800">*</span>
                  </label>
                  <div class="flex items-center">
                    <input
                      id="defaultPurchasingAgentEmailAddress"
                      type="text"
                      name="defaultPurchasingAgentEmailAddress"
                      formControlName="defaultPurchasingAgentEmailAddress"
                      class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-80"
                    />
                  </div>
                  @if (showDefaultPurchasingAgentEmailAddressError) {
                    <div class="text-red-500 mt-2">Please provide a valid default purchasing agent email address</div>
                  }
                </div>
              </div>

              <!-- Default Purchaser Loading Spinner -->
              @if (showDefaultPurchasingAgentSpinner) {
                <div class="pl-2 pr-2 pt-4">
                  <fa-icon [icon]="faGear" animation="spin" class="pl-4 text-slate-600" size="lg"></fa-icon>
                </div>
              }

              <!-- Default Purchasing Agent Info -->
              @if (!showDefaultPurchasingAgentSpinner && isDefaultPurchasingAgentFound && defaultPurchasingAgent) {
                <div class="pr-2 pt-3">
                  <p class="text-agentium_gray-text">{{ defaultPurchasingAgent.firstName }} {{ defaultPurchasingAgent.lastName }}</p>
                  <p class="text-agentium_gray-text">{{ defaultPurchasingAgent.defaultAgencyName }}</p>
                  <p class="text-agentium_gray-text">{{ defaultPurchasingAgent.defaultAgencyAddress }}</p>
                </div>
              }
            </div>
            @if (showDefaultPurchasingAgentNotFoundError) {
              <div class="mt-2 text-red-500">No user found matching the provided email address</div>
            }
          </div>
        }

        <!-- Payment Options -->
        <div class="mb-6">
          <label class="block font-semibold text-agentium_gray-text mb-2">Payment Options</label>
          <div class="flex items-center mb-2">
            <div class="flex">
              <!-- Billed -->
              <div class="mr-4">
                <input
                  type="radio"
                  id="billed"
                  formControlName="paymentOption"
                  value="{{ paymentOptionEnum.BILLED }}"
                  class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 focus:ring-momentum-deep_purple"
                />
                <label for="billed" class="ml-2 cursor-pointer text-agentium_gray-text">Billed</label>
              </div>
              <!-- Debit Prepaid Balance -->
              @if (!showAgencyIdSpinner && isAgencyFound && marketplaceForm.get('premierPartnerMarketplace')?.value) {
                <div>
                  <input
                    type="radio"
                    id="debitPrepaidBalance"
                    formControlName="paymentOption"
                    value="{{ paymentOptionEnum.DEBIT_PREPAID_BALANCE }}"
                    class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 focus:ring-momentum-deep_purple"
                  />
                  <label for="debitPrepaidBalance" class="ml-2 cursor-pointer text-agentium_gray-text">Debit Prepaid Balance</label>
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Referral Price Adjustments -->
        <div class="mb-6">
          <label class="block font-semibold text-agentium_gray-text mb-2">Referral Price Adjustments <span class="text-red-800">*</span> </label>
          <div class="flex items-center mb-2">
            <div class="flex flex-wrap items-center gap-12">
              <!-- % Modifier -->
              <div class="flex items-center">
                <input
                  type="radio"
                  id="percentModifier"
                  formControlName="priceMode"
                  value="percentModifier"
                  class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 focus:ring-momentum-deep_purple"
                />

                <div class="flex items-center ml-2">
                  <input
                    type="number"
                    step="1"
                    formControlName="percentModifier"
                    class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16"
                    (keydown)="preventDecimalInput($event)"
                  />
                  @if (showPercentModifierError) {
                    <div class="text-red-500 ml-2">Percent Modifier is required.</div>
                  }
                  @if (showNegativePercentModifierError) {
                    <div class="text-red-500 ml-2">The negative percent modifier cannot exceed abs(-100)%.</div>
                  }
                </div>

                <label for="percentModifier" class="ml-2 cursor-pointer text-agentium_gray-text">% Modifier</label>
              </div>

              <!-- Delivered Price -->
              @if (marketplaceForm.get("marketplaceType")?.value === marketplaceTypeEnum.INSTANT) {
                <div class="flex items-center">
                  <input
                    type="radio"
                    id="deliveredPrice"
                    formControlName="priceMode"
                    value="deliveredPrice"
                    class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 focus:ring-momentum-deep_purple"
                  />

                  <div class="flex items-center ml-2">
                    <span class="text-agentium_gray-text mr-1">$</span>
                    <input type="number" formControlName="deliveredPrice" class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16" />
                    @if (showDeliveredPriceError) {
                      <div class="text-red-500 ml-2">Delivered Price is required.</div>
                    }
                  </div>

                  <label for="deliveredPrice" class="ml-2 cursor-pointer text-agentium_gray-text">Delivered Price</label>
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Referral Price Range -->
        <div class="mb-6" formGroupName="riskAppetite">
          <label class="block font-semibold text-agentium_gray-text mb-2">Referral Price Range</label>
          <p class="text-sm text-agentium_gray-text mb-2">Only deliver referrals within this price range. Leave either field blank if no limit should apply</p>
          <div class="flex items-center mb-2">
            <div class="flex flex-wrap items-center gap-4">
              <!-- Min. Price-->
              <div class="flex items-center">
                <label for="referralPriceMin" class="text-agentium_gray-text">Min. Price</label>
                <div class="flex items-center ml-2">
                  <span class="text-agentium_gray-text mr-1">$</span>
                  <input id="referralPriceMin" type="number" formControlName="referralPriceMin" class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-20" />
                  @if (showMinReferralPriceError) {
                    <div class="text-red-500 ml-2">Min Price cannot be less than 0.</div>
                  }
                </div>
              </div>

              <!-- Max. Price-->
              <div class="flex items-center">
                <label for="referralPriceMax" class="text-agentium_gray-text">Max. Price</label>
                <div class="flex items-center ml-2">
                  <span class="text-agentium_gray-text mr-1">$</span>
                  <input id="referralPriceMax" type="number" formControlName="referralPriceMax" class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-20" />
                  @if (showMaxReferralPriceError) {
                    <div class="text-red-500 ml-2">Max Price cannot be less than 0.</div>
                  }
                </div>
              </div>
            </div>
            @if (showReferralPriceError) {
              <div class="text-red-500 ml-2">Min price cannot be greater than max price.</div>
            }
          </div>
        </div>

        <!-- Referral Sources-->
        <div class="mb-6" formGroupName="riskAppetite">
          <div class="flex items-center mb-2 whitespace-nowrap">
          <label class="block font-semibold text-agentium_gray-text mb-2">Referral Sources <span class="text-red-800">*</span> </label>
            <div class="ml-4 flex items-center space-x-2 text-sm">
            <span class="text-momentum-deep_purple cursor-pointer hover:underline -translate-y-1" (click)="selectAllReferralSources()">
            Select All
            </span>
              <span class="text-momentum-deep_purple cursor-pointer hover:underline -translate-y-1" (click)="clearAllReferralSources()">
            Clear Selection
            </span>
            </div>
            </div>
          <div class="grid grid-flow-col grid-rows-4 grid-cols-5" formGroupName="referralSources">
            @for (source of referralSources; track source.id) {
              @if (source.sourceName) {
                <div class="flex items-center">
                  <input
                    type="checkbox"
                    [formControlName]="source.sourceName"
                    [id]="source.id"
                    class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                  />
                  <label [for]="source.id" class="ml-2 cursor-pointer text-agentium_gray-text">
                    {{ source.sourceName }}
                  </label>
                </div>
              }
            }
          </div>
          @if (showReferralSourceError) {
            <div class="text-red-500 mt-2">At least one referral source must be selected.</div>
          }
        </div>
      </div>

      <!-- ---------------------------------- Appetite Settings Section -------------------------------------------- -->
      <h2 class="text-xl text-momentum-deep_purple font-semibold mb-4">Appetite Settings</h2>

      <!-- General Appetite Section -->
      <div class="flex items-center mb-4">
        <h3 class="text-lg font-semibold mr-2">General Appetite</h3>
        <app-info-modal-button [data]="generalAppetiteInfo"></app-info-modal-button>
      </div>
      <div class="bg-white rounded-lg shadow p-6 mb-6">
        <!-- Toggles -->
        <div class="mb-6 grid grid-flow-col grid-rows-2 gap-x-2 gap-y-3" formGroupName="riskAppetite">
          <!-- Jornaya ID Required -->
          <div class="flex items-center">
            <label class="block font-semibold text-agentium_gray-text mr-3">Jornaya ID Required</label>
            <div class="flex items-center">
              <input type="checkbox" formControlName="journayaIdRequired" id="jornayaToggle" class="sr-only" />
              @let journayaIdRequired = marketplaceForm.get("riskAppetite.journayaIdRequired")?.value;
              <label
                for="jornayaToggle"
                class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                [ngClass]="{ 'bg-momentum-deep_purple': journayaIdRequired, 'bg-gray-300': !journayaIdRequired }"
              >
                <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': journayaIdRequired }"></span>
              </label>
            </div>
          </div>

          <!-- Business Line Required -->
          <div class="flex items-center">
            <label class="block font-semibold text-agentium_gray-text mr-3">Business Line Required</label>
            <div class="flex items-center">
              <input type="checkbox" formControlName="businessLineRequired" id="businessLineToggle" class="sr-only" />
              @let businessLineRequired = marketplaceForm.get("riskAppetite.businessLineRequired")?.value;
              <label
                for="businessLineToggle"
                class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                [ngClass]="{ 'bg-momentum-deep_purple': businessLineRequired, 'bg-gray-300': !businessLineRequired }"
              >
                <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': businessLineRequired }"></span>
              </label>
            </div>
          </div>

          <!-- First Name Required -->
          <div class="flex items-center">
            <label class="block font-semibold text-agentium_gray-text mr-3">First Name Required</label>
            <div class="flex items-center">
              <input type="checkbox" formControlName="firstNameRequired" id="firstNameToggle" class="sr-only" />
              @let firstNameRequired = marketplaceForm.get("riskAppetite.firstNameRequired")?.value;
              <label
                for="firstNameToggle"
                class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                [ngClass]="{ 'bg-momentum-deep_purple': firstNameRequired, 'bg-gray-300': !firstNameRequired }"
              >
                <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': firstNameRequired }"></span>
              </label>
            </div>
          </div>

          <!-- Last Name Required -->
          <div class="flex items-center">
            <label class="block font-semibold text-agentium_gray-text mr-3">Last Name Required</label>
            <div class="flex items-center">
              <input type="checkbox" formControlName="lastNameRequired" id="lastNameToggle" class="sr-only" />
              @let lastNameRequired = marketplaceForm.get("riskAppetite.lastNameRequired")?.value;
              <label
                for="lastNameToggle"
                class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                [ngClass]="{ 'bg-momentum-deep_purple': lastNameRequired, 'bg-gray-300': !lastNameRequired }"
              >
                <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': lastNameRequired }"></span>
              </label>
            </div>
          </div>

          <!-- City Required -->
          <div class="flex items-center">
            <label class="block font-semibold text-agentium_gray-text mr-3">City Required</label>
            <div class="flex items-center">
              <input type="checkbox" formControlName="cityRequired" id="cityToggle" class="sr-only" />
              @let cityRequired = marketplaceForm.get("riskAppetite.cityRequired")?.value;
              <label for="cityToggle" class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer" [ngClass]="{ 'bg-momentum-deep_purple': cityRequired, 'bg-gray-300': !cityRequired }">
                <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': cityRequired }"></span>
              </label>
            </div>
          </div>

          <!-- State Required -->
          <div class="flex items-center">
            <label class="block font-semibold text-agentium_gray-text mr-3">State Required</label>
            <div class="flex items-center">
              <input type="checkbox" formControlName="stateRequired" id="stateToggle" class="sr-only" />
              @let stateRequired = marketplaceForm.get("riskAppetite.stateRequired")?.value;
              <label for="stateToggle" class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer" [ngClass]="{ 'bg-momentum-deep_purple': stateRequired, 'bg-gray-300': !stateRequired }">
                <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': stateRequired }"></span>
              </label>
            </div>
          </div>

          <!-- ZIP Code Required -->
          <div class="flex items-center">
            <label class="block font-semibold text-agentium_gray-text mr-3">ZIP Code Required</label>
            <div class="flex items-center">
              <input type="checkbox" formControlName="postalCodeRequired" id="postalCodeToggle" class="sr-only" />
              @let postalCodeRequired = marketplaceForm.get("riskAppetite.postalCodeRequired")?.value;
              <label
                for="postalCodeToggle"
                class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                [ngClass]="{ 'bg-momentum-deep_purple': postalCodeRequired, 'bg-gray-300': !postalCodeRequired }"
              >
                <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': postalCodeRequired }"></span>
              </label>
            </div>
          </div>

          <!-- Email Address Required -->
          <div class="flex items-center">
            <label class="block font-semibold text-agentium_gray-text mr-3">Email Address Required</label>
            <div class="flex items-center">
              <input type="checkbox" formControlName="emailAddressRequired" id="emailAddressToggle" class="sr-only" />
              @let emailAddressRequired = marketplaceForm.get("riskAppetite.emailAddressRequired")?.value;
              <label
                for="emailAddressToggle"
                class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                [ngClass]="{ 'bg-momentum-deep_purple': emailAddressRequired, 'bg-gray-300': !emailAddressRequired }"
              >
                <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': emailAddressRequired }"></span>
              </label>
            </div>
          </div>

          <!-- Phone Number Required -->
          <div class="flex items-center">
            <label class="block font-semibold text-agentium_gray-text mr-3">Phone Number Required</label>
            <div class="flex items-center">
              <input type="checkbox" formControlName="phoneNumberRequired" id="phoneNumberToggle" class="sr-only" />
              @let phoneNumberRequired = marketplaceForm.get("riskAppetite.phoneNumberRequired")?.value;
              <label
                for="phoneNumberToggle"
                class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                [ngClass]="{ 'bg-momentum-deep_purple': phoneNumberRequired, 'bg-gray-300': !phoneNumberRequired }"
              >
                <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': phoneNumberRequired }"></span>
              </label>
            </div>
          </div>

          <!-- One To One Consent Required -->
          <div class="flex items-center">
            <label class="block font-semibold text-agentium_gray-text mr-3">One To One Consent Required</label>
            <div class="flex items-center">
              <input type="checkbox" formControlName="oneToOneConsentRequired" id="oneToOneConsentToggle" class="sr-only" />
              @let oneToOneConsentRequired = marketplaceForm.get("riskAppetite.oneToOneConsentRequired")?.value;
              <label
                for="oneToOneConsentToggle"
                class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                [ngClass]="{ 'bg-momentum-deep_purple': oneToOneConsentRequired, 'bg-gray-300': !oneToOneConsentRequired }"
              >
                <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': oneToOneConsentRequired }"></span>
              </label>
            </div>
          </div>

          <!-- Captures Own Consumer Consent -->
          <div class="flex items-center">
            <label class="block font-semibold text-agentium_gray-text mr-3">Captures Own Consumer Consent</label>
            <div class="flex items-center">
              <input type="checkbox" formControlName="capturesOwnConsumerConsent" id="capturesOwnConsumerConsentToggle" class="sr-only" />
              @let capturesOwnConsumerConsent = marketplaceForm.get("riskAppetite.capturesOwnConsumerConsent")?.value;
              <label
                for="capturesOwnConsumerConsentToggle"
                class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                [ngClass]="{ 'bg-momentum-deep_purple': capturesOwnConsumerConsent, 'bg-gray-300': !capturesOwnConsumerConsent }"
              >
                <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': capturesOwnConsumerConsent }"></span>
              </label>
            </div>
          </div>

          <!-- Accepts Warm Transfers -->
          <div class="flex items-center">
            <label class="block font-semibold text-agentium_gray-text mr-3">Accepts Warm Transfers</label>
            <div class="flex items-center">
              <input type="checkbox" formControlName="acceptWarmTransfers" id="acceptWarmTransfersToggle" class="sr-only" />
              @let acceptWarmTransfers = marketplaceForm.get("riskAppetite.acceptWarmTransfers")?.value;
              <label
                for="acceptWarmTransfersToggle"
                class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                [ngClass]="{ 'bg-momentum-deep_purple': acceptWarmTransfers, 'bg-gray-300': !acceptWarmTransfers }"
              >
                <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': acceptWarmTransfers }"></span>
              </label>
            </div>
          </div>

          <!-- Accepts Ineligible -->
          <div class="flex items-center">
            <label class="block font-semibold text-agentium_gray-text mr-3">Accepts Ineligible</label>
            <div class="flex items-center">
              <input type="checkbox" formControlName="acceptIneligible" id="acceptIneligibleToggle" class="sr-only" />
              @let acceptIneligible = marketplaceForm.get("riskAppetite.acceptIneligible")?.value;
              <label
                for="acceptIneligibleToggle"
                class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                [ngClass]="{ 'bg-momentum-deep_purple': acceptIneligible, 'bg-gray-300': !acceptIneligible }"
              >
                <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': acceptIneligible }"></span>
              </label>
            </div>
          </div>
        </div>

        <!-- States -->
        <div class="mb-4" formGroupName="riskAppetite">
          <div class="flex items-center mb-2 whitespace-nowrap">
          <label class="block font-semibold text-agentium_gray-text mb-2">States <span class="text-red-800">*</span> </label>
            <div class="ml-4 flex items-center space-x-2 text-sm">
            <span class="text-momentum-deep_purple cursor-pointer hover:underline -translate-y-1" (click)="selectAllStates()">
            Select All
            </span>
            <span class="text-momentum-deep_purple cursor-pointer hover:underline -translate-y-1" (click)="clearAllStates()">
            Clear Selection
            </span>
              </div>
          </div>

          <div class="grid grid-flow-col grid-rows-8 grid-cols-7" formGroupName="states">
            @for (state of usStates; track state.displayName) {
              @if (state.field) {
                <div class="flex items-center">
                  <input
                    type="checkbox"
                    [formControlName]="state.field"
                    [id]="'state-' + state.field"
                    class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                  />
                  <label [for]="'state-' + state.field" class="ml-2 cursor-pointer text-agentium_gray-text">
                    {{ state.displayName }}
                  </label>
                </div>
              }
            }
          </div>
          @if (showStatesError) {
            <div class="text-red-500 mt-2">At least one state must be selected.</div>
          }
        </div>
      </div>

      <!-- Business Line Required Error -->
      @if (showBusinessLineRequiredError) {
        <div class="mt-2 text-red-500 mb-6">At least one business line appetite must be enabled.</div>
      }

      <!-- Commercial Appetite Section -->
      <!-- Toggle -->
      <div class="flex items-center mb-4" formGroupName="riskAppetite">
        <h3 class="text-lg font-semibold mr-2">Commercial Appetite</h3>
        <app-info-modal-button [data]="commercialAppetiteInfo" class="mr-4"></app-info-modal-button>
        <div class="flex items-center">
          <input type="checkbox" formControlName="acceptCommercialLines" id="commercialToggle" class="sr-only" />
          @let acceptCommercialLines = marketplaceForm.get("riskAppetite.acceptCommercialLines")?.value;
          <label
            for="commercialToggle"
            class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
            [ngClass]="{
              'bg-momentum-deep_purple': acceptCommercialLines,
              'bg-gray-300': !acceptCommercialLines
            }"
          >
            <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': acceptCommercialLines }"></span>
          </label>
        </div>
      </div>

      <!-- Commercial Appetite Input Fields -->
      <div class="bg-white rounded-lg shadow p-6 mb-6" formGroupName="riskAppetite">
        @if (marketplaceForm.get("riskAppetite.acceptCommercialLines")?.value) {
          <div>
            <!-- NAICS -->
            <div class="mb-6">
              <label class="block font-semibold text-agentium_gray-text mb-2">NAICS</label>
              <div class="flex items-center mb-2">
                <div class="flex">
                  <div class="mr-4">
                    <input
                      type="radio"
                      id="naicsExclude"
                      formControlName="naicsMode"
                      value="exclude"
                      class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 focus:ring-momentum-deep_purple"
                    />
                    <label for="naicsExclude" class="ml-2 cursor-pointer text-agentium_gray-text">Exclude</label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      id="naicsInclude"
                      formControlName="naicsMode"
                      value="include"
                      class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 focus:ring-momentum-deep_purple"
                    />
                    <label for="naicsInclude" class="ml-2 cursor-pointer text-agentium_gray-text">Include</label>
                  </div>
                </div>
              </div>
              <textarea rows="3" formControlName="naicsCodes" class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full h-[74px]" placeholder="123456, 223456, 333456"></textarea>
              @if (naicsInvalid && marketplaceForm.get("riskAppetite.naicsCodes")?.touched) {
                <div class="text-red-500 mt-1">NAICS codes must be 6 digits and separated by commas.</div>
              }
            </div>

            <!-- Appetite Toggles -->
            <div class="mb-6 flex flex-row flex-wrap items-center gap-x-8">
              <!-- Accepts Commercial Insured -->
              <div class="flex items-center">
                <label class="block font-semibold text-agentium_gray-text mr-4">Accepts Commercial Insured</label>
                <div class="flex items-center">
                  <input type="checkbox" formControlName="acceptCommercialInsured" id="acceptCommercialInsuredToggle" class="sr-only" />
                  @let acceptCommercialInsured = marketplaceForm.get("riskAppetite.acceptCommercialInsured")?.value;
                  <label
                    for="acceptCommercialInsuredToggle"
                    class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                    [ngClass]="{ 'bg-momentum-deep_purple': acceptCommercialInsured, 'bg-gray-300': !acceptCommercialInsured }"
                  >
                    <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': acceptCommercialInsured }"></span>
                  </label>
                </div>
              </div>

              <!-- Accepts Commercial Uninsured -->
              <div class="flex items-center justify-between">
                <label class="block font-semibold text-agentium_gray-text mr-4">Accepts Commercial Uninsured</label>
                <div class="flex items-center">
                  <input type="checkbox" formControlName="acceptCommercialUninsured" id="acceptCommercialUninsuredToggle" class="sr-only" />
                  @let acceptCommercialUninsured = marketplaceForm.get("riskAppetite.acceptCommercialUninsured")?.value;
                  <label
                    for="acceptCommercialUninsuredToggle"
                    class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                    [ngClass]="{ 'bg-momentum-deep_purple': acceptCommercialUninsured, 'bg-gray-300': !acceptCommercialUninsured }"
                  >
                    <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': acceptCommercialUninsured }"></span>
                  </label>
                </div>
              </div>

              <!-- Business Address Required -->
              <div class="flex items-center">
                <label class="block font-semibold text-agentium_gray-text mr-4">Business Address Required</label>
                <div class="flex items-center">
                  <input type="checkbox" formControlName="businessAddressRequired" id="businessAddressToggle" class="sr-only" />
                  @let businessAddressRequired = marketplaceForm.get("riskAppetite.businessAddressRequired")?.value;
                  <label
                    for="businessAddressToggle"
                    class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                    [ngClass]="{ 'bg-momentum-deep_purple': businessAddressRequired, 'bg-gray-300': !businessAddressRequired }"
                  >
                    <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': businessAddressRequired }"></span>
                  </label>
                </div>
              </div>

              <!-- Business Name Required -->
              <div class="flex items-center justify-between">
                <label class="block font-semibold text-agentium_gray-text mr-4">Business Name Required</label>
                <div class="flex items-center">
                  <input type="checkbox" formControlName="businessNameRequired" id="businessNameToggle" class="sr-only" />
                  @let businessNameRequired = marketplaceForm.get("riskAppetite.businessNameRequired")?.value;
                  <label
                    for="businessNameToggle"
                    class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                    [ngClass]="{ 'bg-momentum-deep_purple': businessNameRequired, 'bg-gray-300': !businessNameRequired }"
                  >
                    <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': businessNameRequired }"></span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Commercial Business Lines -->
            <div class="mb-6">
              <label class="block font-semibold text-agentium_gray-text mb-2">Commercial Business Lines</label>
              <!-- Commercial Business Lines Min and Max Limits -->
              <p class="text-sm text-agentium_gray-text mb-2">leave blank if there are no limits</p>
              <div class="flex flex-col items-start">
                <!-- Minimum -->
                <div class="flex items-center mb-4">
                  <label for="commercialBusinessLinesMin" class="block text-agentium_gray-text text-sm mb-1 mr-3">Minimum</label>
                  <input
                    type="number"
                    id="commercialBusinessLinesMin"
                    formControlName="commercialBusinessLinesMin"
                    class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16"
                    min="0"
                    max="99"
                    (input)="limitDigits($event, 2)"
                  />
                  @if (showMinCommercialBusinessLinesError) {
                    <div class="text-red-500 ml-2">Min number of business lines cannot be less than 0.</div>
                  }
                </div>
                <!-- Maximum -->
                <div class="flex items-center">
                  <label for="commercialBusinessLinesMax" class="block text-agentium_gray-text text-sm mb-1 mr-2">Maximum</label>
                  <input
                    type="number"
                    id="commercialBusinessLinesMax"
                    formControlName="commercialBusinessLinesMax"
                    class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16"
                    min="0"
                    max="99"
                    (input)="limitDigits($event, 2)"
                  />
                  @if (showMaxCommercialBusinessLinesError) {
                    <div class="text-red-500 ml-2">Max number of business lines cannot be less than 0.</div>
                  }
                </div>
                @if (showCommercialBusinessLinesError) {
                  <div class="text-red-500 mt-2">Min number of business lines cannot be greater than max number of business lines.</div>
                }
              </div>

              <!-- Commercial Risk Appetite Business Line Table -->
              <div class="mt-6">
                <app-business-line-appetite
                  #commercialBusinessLineAppetiteComponent
                  [isNewMarketplaceCreationRequest]="isNewMarketplaceCreationRequest"
                  [businessLines]="commercialBusinessLines"
                  [riskAppetiteBusinessLines]="commercialRiskAppetiteBusinessLines"
                  (businessLineAppetiteChanged)="onCommercialBusinessLineAppetiteChanged()"
                ></app-business-line-appetite>
              </div>
            </div>

            <div class="grid grid-flow-col grid-rows-2 grid-cols-2">
              <!-- Years in Business -->
              <div class="mb-6">
                <label class="block font-semibold text-agentium_gray-text mb-2">Years in Business</label>
                <p class="text-sm text-agentium_gray-text mb-2">Enter 0 for New Business through 99 for maximum, leave blank if there are no limits</p>
                <div class="flex flex-col items-start">
                  <!-- Minimum -->
                  <div class="flex items-center mb-4">
                    <input
                      type="number"
                      id="yearsInBusinessMin"
                      formControlName="yearsInBusinessMin"
                      class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16 mr-2"
                      min="0"
                      max="99"
                      (input)="limitDigits($event, 2)"
                    />
                    <label for="yearsInBusinessMin" class="block text-agentium_gray-text text-sm mb-1">Minimum</label>
                    @if (showMinYearsInBusinessError) {
                      <div class="text-red-500 ml-2">Min years in business cannot be less than 0.</div>
                    }
                  </div>
                  <!-- Maximum -->
                  <div class="flex items-center">
                    <input
                      type="number"
                      id="yearsInBusinessMax"
                      formControlName="yearsInBusinessMax"
                      class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16 mr-2"
                      min="0"
                      max="99"
                      (input)="limitDigits($event, 2)"
                    />
                    <label for="yearsInBusinessMax" class="block text-agentium_gray-text text-sm mb-1">Maximum</label>
                    @if (showMaxYearsInBusinessError) {
                      <div class="text-red-500 ml-2">Max years in business cannot be less than 0.</div>
                    }
                  </div>
                  @if (showYearsInBusinessError) {
                    <div class="text-red-500 mt-2">Min years in business cannot be greater than max years in business.</div>
                  }
                </div>
              </div>

              <!-- Commercial Estimated Premium -->
              <div class="mb-4">
                <label class="block font-semibold text-agentium_gray-text mb-2">Estimated Premium</label>
                <p class="text-sm text-agentium_gray-text mb-2">Enter the estimated premium</p>
                <div class="flex flex-col items-start">
                  <!-- Minimum -->
                  <div class="flex items-center mb-4">
                    <input
                      type="number"
                      id="commercialEstimatedPremiumMin"
                      formControlName="commercialEstimatedPremiumMin"
                      class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-24 mr-2"
                      min="0"
                    />
                    <label for="commercialEstimatedPremiumMin" class="block text-agentium_gray-text text-sm mb-1">Minimum</label>
                    @if (showMinCommercialEstimatedPremiumError) {
                      <div class="text-red-500 ml-2">Min estimated premium cannot be less than 0.</div>
                    }
                  </div>
                  <!-- Maximum -->
                  <div class="flex items-center">
                    <input
                      type="number"
                      id="commercialEstimatedPremiumMax"
                      formControlName="commercialEstimatedPremiumMax"
                      class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-24 mr-2"
                      min="0"
                    />
                    <label for="commercialEstimatedPremiumMax" class="block text-agentium_gray-text text-sm mb-1">Maximum</label>
                    @if (showMaxCommercialEstimatedPremiumError) {
                      <div class="text-red-500 ml-2">Max estimated premium cannot be less than 0.</div>
                    }
                  </div>
                  @if (showCommercialEstimatedPremiumError) {
                    <div class="text-red-500 mt-2">Min estimated premium cannot be greater than max estimated premium.</div>
                  }
                </div>
              </div>

              <!-- Number of Employees -->
              <div class="mb-4">
                <label class="block font-semibold text-agentium_gray-text mb-2">Number of Employees</label>
                <p class="text-sm text-agentium_gray-text mb-2">Enter 0 for sole proprietor through 999 for maximum, leave blank if there are no limits</p>
                <div class="flex flex-col items-start">
                  <!-- Minimum -->
                  <div class="flex items-center mb-4">
                    <input
                      type="number"
                      id="numberOfEmployeesMin"
                      formControlName="numberOfEmployeesMin"
                      class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16 mr-2"
                      min="0"
                      max="999"
                      (input)="limitDigits($event, 3)"
                    />
                    <label for="numberOfEmployeesMin" class="block text-agentium_gray-text text-sm mb-1">Minimum</label>
                    @if (showMinNumberOfEmployeesError) {
                      <div class="text-red-500 ml-2">Min number of employees cannot be less than 0.</div>
                    }
                  </div>
                  <!-- Maximum -->
                  <div class="flex items-center">
                    <input
                      type="number"
                      id="numberOfEmployeesMax"
                      formControlName="numberOfEmployeesMax"
                      class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16 mr-2"
                      min="0"
                      max="999"
                      (input)="limitDigits($event, 3)"
                    />
                    <label for="numberOfEmployeesMax" class="block text-agentium_gray-text text-sm mb-1">Maximum</label>
                    @if (showMaxNumberOfEmployeesError) {
                      <div class="text-red-500 ml-2">Max number of employees cannot be less than 0.</div>
                    }
                  </div>
                  @if (showNumberOfEmployeesError) {
                    <div class="text-red-500 mt-2">Min number of employees cannot be greater than max number of employees.</div>
                  }
                </div>
              </div>
            </div>
          </div>
        }
      </div>

      <!-- Personal Appetite Section -->
      <!-- Toggle -->
      <div class="flex items-center mb-4" formGroupName="riskAppetite">
        <h3 class="text-lg font-semibold mr-2">Personal Appetite</h3>
        <app-info-modal-button [data]="personalAppetiteInfo" class="mr-4"></app-info-modal-button>
        <div class="flex items-center">
          <input type="checkbox" formControlName="acceptPersonalLines" id="personalToggle" class="sr-only" />
          @let acceptPersonalLines = marketplaceForm.get("riskAppetite.acceptPersonalLines")?.value;
          <label
            for="personalToggle"
            class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
            [ngClass]="{ 'bg-momentum-deep_purple': acceptPersonalLines, 'bg-gray-300': !acceptPersonalLines }"
          >
            <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': acceptPersonalLines }"></span>
          </label>
        </div>
      </div>

      <!-- Personal Appetite Input Fields -->
      <div class="bg-white rounded-lg shadow p-6 mb-6" formGroupName="riskAppetite">
        @if (marketplaceForm.get("riskAppetite.acceptPersonalLines")?.value) {
          <div>
            <!-- Appetite Toggles -->
            <div class="mb-6 grid grid-flow-col grid-rows-1 grid-cols-2 gap-x-10 max-w-[580px]">
              <!-- Accepts Personal Insured -->
              <div class="flex items-center">
                <label class="block font-semibold text-agentium_gray-text mr-4">Accepts Personal Insured</label>
                <div class="flex items-center">
                  <input type="checkbox" formControlName="acceptPersonalInsured" id="acceptPersonalInsuredToggle" class="sr-only" />
                  @let acceptPersonalInsured = marketplaceForm.get("riskAppetite.acceptPersonalInsured")?.value;
                  <label
                    for="acceptPersonalInsuredToggle"
                    class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                    [ngClass]="{ 'bg-momentum-deep_purple': acceptPersonalInsured, 'bg-gray-300': !acceptPersonalInsured }"
                  >
                    <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': acceptPersonalInsured }"></span>
                  </label>
                </div>
              </div>

              <!-- Accepts Personal Uninsured -->
              <div class="flex items-center justify-between">
                <label class="block font-semibold text-agentium_gray-text mr-4">Accepts Personal Uninsured</label>
                <div class="flex items-center">
                  <input type="checkbox" formControlName="acceptPersonalUninsured" id="acceptPersonalUninsuredToggle" class="sr-only" />
                  @let acceptPersonalUninsured = marketplaceForm.get("riskAppetite.acceptPersonalUninsured")?.value;
                  <label
                    for="acceptPersonalUninsuredToggle"
                    class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                    [ngClass]="{ 'bg-momentum-deep_purple': acceptPersonalUninsured, 'bg-gray-300': !acceptPersonalUninsured }"
                  >
                    <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': acceptPersonalUninsured }"></span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Personal Business Lines -->
            <div class="mb-6">
              <label class="block font-semibold text-agentium_gray-text mb-2">Personal Business Lines</label>
              <!-- Personal Business Lines Min and Max Limits -->
              <p class="text-sm text-agentium_gray-text mb-2">leave blank if there are no limits</p>
              <div class="flex flex-col items-start">
                <!-- Minimum -->
                <div class="flex items-center mb-4">
                  <label for="personalBusinessLinesMin" class="block text-agentium_gray-text text-sm mb-1 mr-3">Minimum</label>
                  <input
                    type="number"
                    id="personalBusinessLinesMin"
                    formControlName="personalBusinessLinesMin"
                    class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16"
                    min="0"
                    max="99"
                    (input)="limitDigits($event, 2)"
                  />
                  @if (showMinPersonalBusinessLinesError) {
                    <div class="text-red-500 ml-2">Min number of business lines cannot be less than 0.</div>
                  }
                </div>
                <!-- Maximum -->
                <div class="flex items-center">
                  <label for="personalBusinessLinesMax" class="block text-agentium_gray-text text-sm mb-1 mr-2">Maximum</label>
                  <input
                    type="number"
                    id="personalBusinessLinesMax"
                    formControlName="personalBusinessLinesMax"
                    class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16"
                    min="0"
                    max="99"
                    (input)="limitDigits($event, 2)"
                  />
                  @if (showMaxPersonalBusinessLinesError) {
                    <div class="text-red-500 ml-2">Max number of business lines cannot be less than 0.</div>
                  }
                </div>
                @if (showPersonalBusinessLinesError) {
                  <div class="text-red-500 mt-2">Min number of business lines cannot be greater than max number of business lines.</div>
                }
              </div>

              <!-- Personal Risk Appetite Business Line Table -->
              <div class="mt-6">
                <app-business-line-appetite
                  #personalBusinessLineAppetiteComponent
                  [isNewMarketplaceCreationRequest]="isNewMarketplaceCreationRequest"
                  [businessLines]="personalBusinessLines"
                  [riskAppetiteBusinessLines]="personalRiskAppetiteBusinessLines"
                  (businessLineAppetiteChanged)="onPersonalBusinessLineAppetiteChanged()"
                ></app-business-line-appetite>
              </div>
            </div>

            <!-- Personal Estimated Premium -->
            <div class="mb-4">
              <label class="block font-semibold text-agentium_gray-text mb-2">Estimated Premium</label>
              <p class="text-sm text-agentium_gray-text mb-2">Enter the estimated premium</p>
              <div class="flex flex-col items-start">
                <!-- Minimum -->
                <div class="flex items-center mb-4">
                  <input
                    type="number"
                    id="personalEstimatedPremiumMin"
                    formControlName="personalEstimatedPremiumMin"
                    class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-24 mr-2"
                    min="0"
                  />
                  <label for="personalEstimatedPremiumMin" class="block text-agentium_gray-text text-sm mb-1">Minimum</label>
                  @if (showMinPersonalEstimatedPremiumError) {
                    <div class="text-red-500 ml-2">Min estimated premium cannot be less than 0.</div>
                  }
                </div>
                <!-- Maximum -->
                <div class="flex items-center">
                  <input
                    id="personalEstimatedPremiumMax"
                    type="number"
                    formControlName="personalEstimatedPremiumMax"
                    class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-24 mr-2"
                    min="0"
                  />
                  <label for="personalEstimatedPremiumMax" class="block text-agentium_gray-text text-sm mb-1">Maximum</label>
                  @if (showMaxPersonalEstimatedPremiumError) {
                    <div class="text-red-500 ml-2">Max estimated premium cannot be less than 0.</div>
                  }
                </div>
                @if (showPersonalEstimatedPremiumError) {
                  <div class="text-red-500 mt-2">Min estimated premium cannot be greater than max estimated premium.</div>
                }
              </div>
            </div>
          </div>
        }
      </div>

      <!-- Life & Annuities Appetite Section -->
      <!-- Toggle -->
      <div class="flex items-center mb-4" formGroupName="riskAppetite">
        <h3 class="text-lg font-semibold mr-2">Life & Annuities Appetite</h3>
        <app-info-modal-button [data]="lifeAnnuitiesAppetiteInfo" class="mr-4"></app-info-modal-button>
        <div class="flex items-center">
          <input type="checkbox" formControlName="acceptLifeAnnuitiesLines" id="lifeAnnuitiesToggle" class="sr-only" />
          @let acceptLifeAnnuitiesLines = marketplaceForm.get("riskAppetite.acceptLifeAnnuitiesLines")?.value;
          <label
            for="lifeAnnuitiesToggle"
            class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
            [ngClass]="{
              'bg-momentum-deep_purple': acceptLifeAnnuitiesLines,
              'bg-gray-300': !acceptLifeAnnuitiesLines
            }"
          >
            <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': acceptLifeAnnuitiesLines }"></span>
          </label>
        </div>
      </div>

      <!-- Life & Annuities Appetite Input Fields -->
      <div class="bg-white rounded-lg shadow p-6 mb-6" formGroupName="riskAppetite">
        @if (marketplaceForm.get("riskAppetite.acceptLifeAnnuitiesLines")?.value) {
          <div>
            <!-- Appetite Toggles -->
            <div class="mb-6 grid grid-flow-col grid-rows-1 grid-cols-4 gap-x-10 max-w-[1250px]">
              <!-- Accepts Medical Exam Consent -->
              <div class="flex items-center">
                <label class="block font-semibold text-agentium_gray-text mr-4">Accepts Medical Exam Consent</label>
                <div class="flex items-center">
                  <input type="checkbox" formControlName="acceptMedicalExamConsent" id="acceptMedicalExamConsentToggle" class="sr-only" />
                  @let acceptMedicalExamConsent = marketplaceForm.get("riskAppetite.acceptMedicalExamConsent")?.value;
                  <label
                    for="acceptMedicalExamConsentToggle"
                    class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                    [ngClass]="{ 'bg-momentum-deep_purple': acceptMedicalExamConsent, 'bg-gray-300': !acceptMedicalExamConsent }"
                  >
                    <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': acceptMedicalExamConsent }"></span>
                  </label>
                </div>
              </div>

              <!-- Accepts Medical Exam Refusal -->
              <div class="flex items-center justify-between">
                <label class="block font-semibold text-agentium_gray-text mr-4">Accepts Medical Exam Refusal</label>
                <div class="flex items-center">
                  <input type="checkbox" formControlName="acceptMedicalExamRefusal" id="acceptMedicalExamRefusalToggle" class="sr-only" />
                  @let acceptMedicalExamRefusal = marketplaceForm.get("riskAppetite.acceptMedicalExamRefusal")?.value;
                  <label
                    for="acceptMedicalExamRefusalToggle"
                    class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                    [ngClass]="{ 'bg-momentum-deep_purple': acceptMedicalExamRefusal, 'bg-gray-300': !acceptMedicalExamRefusal }"
                  >
                    <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': acceptMedicalExamRefusal }"></span>
                  </label>
                </div>
              </div>

              <!-- Accepts Tobacco Use Past Year -->
              <div class="flex items-center justify-between">
                <label class="block font-semibold text-agentium_gray-text mr-4">Accepts Tobacco Use Past Year</label>
                <div class="flex items-center">
                  <input type="checkbox" formControlName="acceptTobaccoUsePastYear" id="acceptTobaccoUsePastYearToggle" class="sr-only" />
                  @let acceptTobaccoUsePastYear = marketplaceForm.get("riskAppetite.acceptTobaccoUsePastYear")?.value;
                  <label
                    for="acceptTobaccoUsePastYearToggle"
                    class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                    [ngClass]="{ 'bg-momentum-deep_purple': acceptTobaccoUsePastYear, 'bg-gray-300': !acceptTobaccoUsePastYear }"
                  >
                    <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': acceptTobaccoUsePastYear }"></span>
                  </label>
                </div>
              </div>

              <!-- Accepts No Tobacco Use Past Year -->
              <div class="flex items-center justify-between">
                <label class="block font-semibold text-agentium_gray-text mr-4">Accepts No Tobacco Use Past Year</label>
                <div class="flex items-center">
                  <input type="checkbox" formControlName="acceptNoTobaccoUsePastYear" id="acceptNoTobaccoUsePastYearToggle" class="sr-only" />
                  @let acceptNoTobaccoUsePastYear = marketplaceForm.get("riskAppetite.acceptNoTobaccoUsePastYear")?.value;
                  <label
                    for="acceptNoTobaccoUsePastYearToggle"
                    class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                    [ngClass]="{ 'bg-momentum-deep_purple': acceptNoTobaccoUsePastYear, 'bg-gray-300': !acceptNoTobaccoUsePastYear }"
                  >
                    <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': acceptNoTobaccoUsePastYear }"></span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Life & Annuities Business Lines -->
            <div class="mb-6">
              <label class="block font-semibold text-agentium_gray-text mb-2">Life & Annuities Business Lines</label>
              <!-- Life & Annuities Lines Min and Max Limits -->
              <p class="text-sm text-agentium_gray-text mb-2">leave blank if there are no limits</p>
              <div class="flex flex-col items-start">
                <!-- Minimum -->
                <div class="flex items-center mb-4">
                  <label for="lifeAnnuitiesBusinessLinesMin" class="block text-agentium_gray-text text-sm mb-1 mr-3">Minimum</label>
                  <input
                    type="number"
                    id="lifeAnnuitiesBusinessLinesMin"
                    formControlName="lifeAnnuitiesBusinessLinesMin"
                    class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16"
                    min="0"
                    max="99"
                    (input)="limitDigits($event, 2)"
                  />
                  @if (showMinLifeAnnuitiesBusinessLinesError) {
                    <div class="text-red-500 ml-2">Min number of business lines cannot be less than 0.</div>
                  }
                </div>
                <!-- Maximum -->
                <div class="flex items-center">
                  <label for="lifeAnnuitiesBusinessLinesMax" class="block text-agentium_gray-text text-sm mb-1 mr-2">Maximum</label>
                  <input
                    type="number"
                    id="lifeAnnuitiesBusinessLinesMax"
                    formControlName="lifeAnnuitiesBusinessLinesMax"
                    class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16"
                    min="0"
                    max="99"
                    (input)="limitDigits($event, 2)"
                  />
                  @if (showMaxLifeAnnuitiesBusinessLinesError) {
                    <div class="text-red-500 ml-2">Max number of business lines cannot be less than 0.</div>
                  }
                </div>
                @if (showLifeAnnuitiesBusinessLinesError) {
                  <div class="text-red-500 mt-2">Min number of business lines cannot be greater than max number of business lines.</div>
                }
              </div>

              <!-- Life & Annuities Risk Appetite Business Line Table -->
              <div class="mt-6">
                <app-business-line-appetite
                  #lifeAnnuitiesBusinessLineAppetiteComponent
                  [isNewMarketplaceCreationRequest]="isNewMarketplaceCreationRequest"
                  [businessLines]="lifeAndAnnuitiesBusinessLines"
                  [riskAppetiteBusinessLines]="lifeAnnuitiesRiskAppetiteBusinessLines"
                  (businessLineAppetiteChanged)="onLifeAnnuitiesBusinessLineAppetiteChanged()"
                ></app-business-line-appetite>
              </div>
            </div>

            <!-- Death Benefit -->
            <div class="mb-4">
              <label class="block font-semibold text-agentium_gray-text mb-2">Death Benefit</label>
              <p class="text-sm text-agentium_gray-text mb-2">Enter the death benefit</p>
              <div class="flex flex-col items-start">
                <!-- Minimum -->
                <div class="flex items-center mb-4">
                  <input
                    type="number"
                    id="deathBenefitMin"
                    formControlName="deathBenefitMin"
                    class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-[7rem] mr-2"
                    min="0"
                  />
                  <label for="deathBenefitMin" class="block text-agentium_gray-text text-sm mb-1">Minimum</label>
                  @if (showMinDeathBenefitError) {
                    <div class="text-red-500 ml-2">Min death benefit cannot be less than 0.</div>
                  }
                </div>
                <!-- Maximum -->
                <div class="flex items-center">
                  <input
                    type="number"
                    id="deathBenefitMax"
                    formControlName="deathBenefitMax"
                    class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-[7rem] mr-2"
                    min="0"
                  />
                  <label for="deathBenefitMax" class="block text-agentium_gray-text text-sm mb-1">Maximum</label>
                  @if (showMaxDeathBenefitError) {
                    <div class="text-red-500 ml-2">Max death benefit cannot be less than 0.</div>
                  }
                </div>
                @if (showDeathBenefitError) {
                  <div class="text-red-500 mt-2">Min death benefit cannot be greater than max death benefit.</div>
                }
              </div>
            </div>
          </div>
        }
      </div>

      <!-- ---------------------------------- Delivery Settings Section -------------------------------------------- -->
      <div class="flex items-center mb-4">
        <h2 class="text-xl text-momentum-deep_purple font-semibold mr-2">Delivery Settings</h2>
        <app-info-modal-button [data]="deliverySettingsInfo"></app-info-modal-button>
      </div>
      <div class="bg-white rounded-lg shadow p-6 mb-6">
        <!-- Referral Delivery Limit -->
        <div class="mb-6" formGroupName="riskAppetite">
          <label class="block font-semibold text-agentium_gray-text mb-2">Referral Delivery Limit</label>
          <p class="text-sm text-agentium_gray-text mb-2">Enter the maximum number of referrals allowed within an hour, day, week and/or month, leave empty if there are no limits.</p>
          <div class="grid grid-flow-col grid-rows-2 grid-cols-2 gap-y-4 max-w-[520px]">
            <!-- Referrals per hour -->
            <div class="flex flex-col">
              <div class="flex items-center">
                <input type="number" id="maxReferralsPerHour" formControlName="maxReferralsPerHour" class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16" />
                <label for="maxReferralsPerHour" class="ml-2 text-agentium_gray-text">Referrals <span class="font-semibold"> per hour </span> </label>
              </div>
              @if (showReferralsPerHourError) {
                <div class="mt-2 text-red-500">Referrals per hour cannot be less than 1.</div>
              }
            </div>
            <!-- Referrals per day -->
            <div class="flex flex-col">
              <div class="flex items-center">
                <input type="number" id="maxReferralsPerDay" formControlName="maxReferralsPerDay" class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16" />
                <label for="maxReferralsPerDay" class="ml-2 text-agentium_gray-text">Referrals <span class="font-semibold"> per day </span> </label>
              </div>
              @if (showReferralsPerDayError) {
                <div class="mt-2 text-red-500">Referrals per day cannot be less than 1.</div>
              }
            </div>
            <!-- Referrals per week -->
            <div class="flex flex-col">
              <div class="flex items-center">
                <input type="number" id="maxReferralsPerWeek" formControlName="maxReferralsPerWeek" class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16" />
                <label for="maxReferralsPerWeek" class="ml-2 text-agentium_gray-text">Referrals <span class="font-semibold"> per week </span> </label>
              </div>
              @if (showReferralsPerWeekError) {
                <div class="mt-2 text-red-500">Referrals per week cannot be less than 1.</div>
              }
            </div>
            <!-- Referrals per month -->
            <div class="flex flex-col">
              <div class="flex items-center">
                <input type="number" id="maxReferralsPerMonth" formControlName="maxReferralsPerMonth" class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-16" />
                <label for="maxReferralsPerMonth" class="ml-2 text-agentium_gray-text">Referrals <span class="font-semibold"> per month </span> </label>
              </div>
              @if (showReferralsPerMonthError) {
                <div class="mt-2 text-red-500">Referrals per month cannot be less than 1.</div>
              }
            </div>
          </div>
        </div>

        <!-- Referral Delivery Days -->
        <div class="mb-6" formGroupName="riskAppetite">
          <label class="block font-semibold text-agentium_gray-text mt-4 mb-2">Referral Delivery Days</label>
          <div class="flex flex-col items-start">
            <!-- Days -->
            <div class="grid grid-cols-7 gap-2 max-w-[1000px]">
              <!-- Monday -->
              <div class="flex items-center">
                <input
                  type="checkbox"
                  id="acceptReferralsOnMonday"
                  formControlName="acceptReferralsOnMonday"
                  class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                />
                <label for="acceptReferralsOnMonday" class="ml-2 cursor-pointer text-agentium_gray-text"> Monday </label>
              </div>
              <!-- Tuesday -->
              <div class="flex items-center">
                <input
                  type="checkbox"
                  id="acceptReferralsOnTuesday"
                  formControlName="acceptReferralsOnTuesday"
                  class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                />
                <label for="acceptReferralsOnTuesday" class="ml-2 cursor-pointer text-agentium_gray-text"> Tuesday </label>
              </div>
              <!-- Wednesday -->
              <div class="flex items-center">
                <input
                  type="checkbox"
                  id="acceptReferralsOnWednesday"
                  formControlName="acceptReferralsOnWednesday"
                  class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                />
                <label for="acceptReferralsOnWednesday" class="ml-2 cursor-pointer text-agentium_gray-text"> Wednesday </label>
              </div>
              <!-- Thursday -->
              <div class="flex items-center">
                <input
                  type="checkbox"
                  id="acceptReferralsOnThursday"
                  formControlName="acceptReferralsOnThursday"
                  class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                />
                <label for="acceptReferralsOnThursday" class="ml-2 cursor-pointer text-agentium_gray-text"> Thursday </label>
              </div>
              <!-- Friday -->
              <div class="flex items-center">
                <input
                  type="checkbox"
                  id="acceptReferralsOnFriday"
                  formControlName="acceptReferralsOnFriday"
                  class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                />
                <label for="acceptReferralsOnFriday" class="ml-2 cursor-pointer text-agentium_gray-text"> Friday </label>
              </div>
              <!-- Saturday -->
              <div class="flex items-center">
                <input
                  type="checkbox"
                  id="acceptReferralsOnSaturday"
                  formControlName="acceptReferralsOnSaturday"
                  class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                />
                <label for="acceptReferralsOnSaturday" class="ml-2 cursor-pointer text-agentium_gray-text"> Saturday </label>
              </div>
              <!-- Sunday -->
              <div class="flex items-center">
                <input
                  type="checkbox"
                  id="acceptReferralsOnSunday"
                  formControlName="acceptReferralsOnSunday"
                  class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                />
                <label for="acceptReferralsOnSunday" class="ml-2 cursor-pointer text-agentium_gray-text"> Sunday </label>
              </div>
              @if (showDeliveryDaysError) {
                <div class="mt-2 text-red-500">At least one delivery day must be selected.</div>
              }
            </div>

            <!-- Holiday Delivery Suppression -->
            <div class="flex items-center gap-3 mt-4 pt-4 border-t border-dashed border-gray-200">
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  id="suppressDeliveryOnHolidays"
                  class="sr-only peer"
                  [checked]="isHolidaySuppressionOn()"
                  [disabled]="marketplaceForm.get('riskAppetite.acceptReferralsOnBankHolidays')?.disabled ?? false"
                  (change)="toggleHolidaySuppression($event)"
                />
                <div
                  class="w-10 h-5 bg-gray-300 rounded-full peer peer-checked:bg-momentum-deep_purple peer-disabled:opacity-50 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all after:shadow peer-checked:after:translate-x-5"
                ></div>
              </label>
              <label for="suppressDeliveryOnHolidays" class="cursor-pointer text-agentium_gray-text"> Suppress delivery on holidays </label>
              <span class="text-xs text-gray-400"> {{ holidaySummary }} </span>
              <button type="button" class="ml-auto text-sm font-semibold text-momentum-royal_purple hover:underline" (click)="openHolidayModal()">
                Edit holidays
              </button>
            </div>

            <!-- Holiday selection modal -->
            @if (isHolidayModalOpen) {
              <div class="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/45 px-4 py-10" (click)="closeHolidayModal()">
                <div
                  class="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="holidayModalTitle"
                  (click)="$event.stopPropagation()"
                >
                  <!-- Header -->
                  <div class="border-b border-gray-200 px-6 pb-4 pt-5">
                    <div class="flex items-start justify-between">
                      <h2 id="holidayModalTitle" class="text-lg font-semibold text-momentum-deep_purple">Holidays</h2>
                      <button type="button" class="rounded px-2 text-xl leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close" (click)="closeHolidayModal()">
                        &#10005;
                      </button>
                    </div>
                    <p class="mt-2 text-xs leading-5 text-gray-500">
                      Check the holidays your partners are closed. Referrals are held on checked days and resume on the next delivery day.
                    </p>
                  </div>

                  <!-- SECTION 1: U.S. bank holidays -->
                  <div class="border-b border-gray-100">
                    <button
                      type="button"
                      class="flex w-full items-center gap-3 px-6 py-3.5 text-left hover:bg-purple-50/40"
                      [attr.aria-expanded]="bankSectionOpen"
                      (click)="toggleBankSection()"
                    >
                      <span class="inline-block w-3 text-base leading-none text-gray-400 transition-transform" [class.rotate-90]="bankSectionOpen">&#8250;</span>
                      <span class="flex-1">
                        <span class="block text-sm font-semibold text-momentum-deep_purple">U.S. bank holidays</span>
                        <span class="mt-0.5 block text-[11px] text-gray-400">{{ suppressedHolidays.length }} of {{ selectableHolidays.length }} suppressed</span>
                      </span>
                    </button>

                    @if (bankSectionOpen) {
                      <!-- Select all / Clear all bar -->
                      <div class="flex items-center gap-4 px-6 py-2 text-xs">
                        <button type="button" class="font-semibold text-momentum-royal_purple hover:underline" (click)="selectAllHolidays()">Select all</button>
                        <button type="button" class="font-semibold text-momentum-royal_purple hover:underline" (click)="clearAllHolidays()">Clear all</button>
                        <span class="ml-auto text-gray-500"> {{ holidayModalSelection.size }} of {{ selectableHolidays.length }} selected </span>
                      </div>

                      <!-- Holiday list -->
                      <div class="px-3 pb-2">
                        @for (holiday of selectableHolidays; track holiday.bankHoliday) {
                          <label class="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 odd:bg-gray-50/60 hover:bg-purple-50" [for]="'holiday-' + holiday.bankHoliday">
                            <input
                              type="checkbox"
                              [id]="'holiday-' + holiday.bankHoliday"
                              class="h-4 w-4 cursor-pointer rounded border-gray-300 text-momentum-deep_purple focus:ring-momentum-deep_purple"
                              [checked]="holidayModalSelection.has(holiday.bankHoliday!)"
                              (change)="toggleHolidayModalSelection(holiday.bankHoliday!)"
                            />
                            <span class="flex-1 text-sm text-gray-800">
                              {{ holiday.description }}
                              @if (holiday.hint) {
                                <span class="ml-2 rounded-full border border-yellow-200 bg-yellow-50 px-2 py-0.5 align-middle text-[10px] font-semibold text-yellow-700">
                                  {{ holiday.hint }}
                                </span>
                              }
                            </span>
                            <span class="whitespace-nowrap text-xs text-gray-400">{{ holiday.dateRule }}</span>
                          </label>
                        }
                      </div>
                    }
                  </div>

                  <!-- SECTION 2: Custom holidays -->
                  <div class="border-b border-gray-100">
                    <button
                      type="button"
                      class="flex w-full items-center gap-3 px-6 py-3.5 text-left hover:bg-purple-50/40"
                      [attr.aria-expanded]="customSectionOpen"
                      (click)="toggleCustomSection()"
                    >
                      <span class="inline-block w-3 text-base leading-none text-gray-400 transition-transform" [class.rotate-90]="customSectionOpen">&#8250;</span>
                      <span class="flex-1">
                        <span class="block text-sm font-semibold text-momentum-deep_purple">Custom holidays</span>
                        <span class="mt-0.5 block text-[11px] text-gray-400">{{ activeCustomHolidayModalCount }} on</span>
                      </span>
                    </button>

                    @if (customSectionOpen) {
                      <div class="px-4 pb-3">
                        <p class="px-2 pb-2 pt-1 text-xs leading-5 text-gray-500">
                          Add holidays your partners observe that are not on the bank list, like Christmas Eve. Each one repeats every year. Turn any off to pause it without losing it.
                        </p>

                        <!-- Add a custom holiday -->
                        <div class="flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 bg-gray-50/70 p-3">
                          <input
                            type="text"
                            [(ngModel)]="newCustomHolidayName"
                            [ngModelOptions]="{ standalone: true }"
                            (keyup.enter)="addCustomHoliday()"
                            placeholder="Holiday name, e.g. Christmas Eve"
                            aria-label="Custom holiday name"
                            class="min-w-[150px] flex-1 rounded-lg border border-gray-300 p-2 text-sm text-agentium_gray-text"
                          />
                          <select
                            [(ngModel)]="newCustomHolidayMonth"
                            [ngModelOptions]="{ standalone: true }"
                            (ngModelChange)="onNewHolidayMonthChange()"
                            aria-label="Month"
                            class="cursor-pointer rounded-lg border border-gray-300 p-2 text-sm text-agentium_gray-text"
                          >
                            @for (month of monthOptions; track month) {
                              <option [ngValue]="month">{{ monthNames[month - 1] }}</option>
                            }
                          </select>
                          <select
                            [(ngModel)]="newCustomHolidayDay"
                            [ngModelOptions]="{ standalone: true }"
                            aria-label="Day"
                            class="cursor-pointer rounded-lg border border-gray-300 p-2 text-sm text-agentium_gray-text"
                          >
                            @for (day of validDayOptions; track day) {
                              <option [ngValue]="day">{{ day }}</option>
                            }
                          </select>
                          <button type="button" class="rounded-lg bg-momentum-royal_purple px-4 py-2 text-sm font-semibold text-white hover:brightness-105" (click)="addCustomHoliday()">
                            Add
                          </button>
                        </div>

                        @if (customHolidayError) {
                          <p class="px-2 pt-1.5 text-xs text-red-500">{{ customHolidayError }}</p>
                        }

                        <!-- Custom holiday list -->
                        @if (sortedCustomHolidayModalSelection.length > 0) {
                          <div class="mt-1">
                            @for (customHoliday of sortedCustomHolidayModalSelection; track customHoliday) {
                              <div class="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-purple-50" [class.opacity-60]="!customHoliday.enabled">
                                <input
                                  type="checkbox"
                                  class="h-4 w-4 cursor-pointer rounded border-gray-300 text-momentum-deep_purple focus:ring-momentum-deep_purple"
                                  [checked]="customHoliday.enabled"
                                  (change)="toggleCustomHolidayEnabled(customHoliday)"
                                  [attr.aria-label]="customHoliday.name + ' enabled'"
                                />
                                <span class="flex-1 text-sm" [class.text-gray-800]="customHoliday.enabled" [class.text-gray-400]="!customHoliday.enabled">
                                  {{ customHoliday.name }}
                                  @if (!customHoliday.enabled) {
                                    <span class="ml-2 rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 align-middle text-[10px] font-semibold text-gray-500">Off</span>
                                  }
                                </span>
                                <span class="whitespace-nowrap text-xs text-gray-400">{{ monthNames[customHoliday.month - 1] }} {{ customHoliday.day }}</span>
                                <button
                                  type="button"
                                  class="rounded px-1.5 text-base leading-none text-gray-400 hover:bg-red-50 hover:text-red-600"
                                  [attr.aria-label]="'Remove ' + customHoliday.name"
                                  (click)="removeCustomHoliday(customHoliday)"
                                >
                                  &#10005;
                                </button>
                              </div>
                            }
                          </div>
                        } @else {
                          <div class="mx-2 mt-2 rounded-xl border border-dashed border-gray-300 py-4 text-center text-xs text-gray-400">
                            No custom holidays yet. Add one above.
                          </div>
                        }
                      </div>
                    }
                  </div>

                  <!-- Footer -->
                  <div class="flex justify-end gap-2.5 border-t border-gray-200 px-6 py-4">
                    <button type="button" class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-600 hover:bg-gray-50" (click)="closeHolidayModal()">
                      Cancel
                    </button>
                    <button type="button" class="rounded-lg bg-momentum-deep_purple px-4 py-2 text-sm font-semibold text-white hover:brightness-110" (click)="saveHolidayModal()">
                      Save changes
                    </button>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>

        <!-- Referral Delivery Times -->
        <div class="mb-6" formGroupName="riskAppetite">
          <label class="block font-semibold text-agentium_gray-text mt-4 mb-2">Referral Delivery Times</label>
          <div class="flex items-center gap-4">
            <!-- Start -->
            <div class="flex items-center">
              <label for="referralDeliveryStartTime" class="mr-2 cursor-pointer text-agentium_gray-text">Start</label>
              <input
                type="time"
                id="referralDeliveryStartTime"
                formControlName="referralDeliveryStartTime"
                class="border rounded p-2 w-full text-agentium_gray-text border-gray-300 disabled:bg-gray-300"
              />
            </div>
            <!-- End -->
            <div class="flex items-center">
              <label for="referralDeliveryEndTime" class="mr-2 cursor-pointer text-agentium_gray-text">End</label>
              <input
                type="time"
                id="referralDeliveryEndTime"
                formControlName="referralDeliveryEndTime"
                class="border rounded p-2 w-full text-agentium_gray-text border-gray-300 disabled:bg-gray-300"
              />
            </div>
            <!-- Timezone -->
            <div class="flex items-center">
              <label for="referralDeliveryTimezone" class="mr-2 cursor-pointer text-agentium_gray-text">Timezone</label>
              <select
                id="referralDeliveryTimezone"
                formControlName="referralDeliveryTimezone"
                class="cursor-pointer border rounded p-2 w-full text-agentium_gray-text disabled:bg-gray-300 border-gray-300"
              >
                <option value="America/Anchorage">Alaska</option>
                <option value="America/Chicago" selected>Central</option>
                <option value="America/New_York">Eastern</option>
                <option value="Pacific/Honolulu">Hawaii</option>
                <option value="America/Denver">Mountain</option>
                <option value="America/Los_Angeles">Pacific</option>
              </select>
            </div>
            <!-- 24 hrs/day -->
            <div class="flex items-center">
              <input
                type="checkbox"
                id="accept24hrReferralDelivery"
                formControlName="accept24hrReferralDelivery"
                class="mr-1 w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
              />
              <label for="accept24hrReferralDelivery" class="ml-2 cursor-pointer text-agentium_gray-text"> 24 hrs/day</label>
            </div>
          </div>
        </div>

        <!-- Referral Delivery Method -->
        <div class="mb-6 flex flex-col">
          <div class="block font-semibold text-agentium_gray-text mb-4">Referral Delivery Method</div>

          <!-- Agency Team Members Note -->
          @if (!showAgencyIdSpinner && isAgencyFound) {
            <p class="text-sm text-agentium_gray-text mt-[-10px] mb-4">Agency Team Members must have the role 'Admin' to view all delivered referrals in the agency portal.</p>
          }

          <!-- API Delivery Toggle -->
          <div class="mb-6">
            <div class="flex items-center">
              <label class="block font-semibold text-agentium_gray-text mr-4">API Delivery</label>
              <div class="flex items-center">
                <input type="checkbox" formControlName="deliverToApi" id="deliverToApiToggle" class="sr-only" />
                @let deliverToApi = marketplaceForm.get("deliverToApi")?.value;
                <label
                  for="deliverToApiToggle"
                  class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                  [ngClass]="{
                    'bg-momentum-deep_purple': deliverToApi,
                    'bg-gray-300': !deliverToApi
                  }"
                >
                  <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': deliverToApi }"></span>
                </label>
              </div>
            </div>

            @if (marketplaceForm.get("deliverToApi")?.value) {
              <!-- Generic API Delivery -->
              <div class="ml-2 mt-6 mb-6 flex flex-col">
                <div class="flex flex-col">
                  <label class="block font-semibold text-agentium_gray-text mb-2">API Delivery Method <span class="text-red-800">*</span></label>
                  <div class="flex flex-wrap gap-x-8 gap-y-2">
                    @for (method of referralDeliveryMethodOptions; track method) {
                      <div class="flex items-center">
                        <input
                          type="radio"
                          [id]="'referralMethod-' + method"
                          formControlName="referralDeliveryMethod"
                          [value]="method"
                          class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 focus:ring-momentum-deep_purple"
                        />
                        <label [for]="'referralMethod-' + method" class="ml-2 cursor-pointer text-agentium_gray-text">
                          {{ getReferralDisplayLabel(method) }}
                        </label>
                      </div>
                    }
                  </div>

                  @if (marketplaceForm.get("referralDeliveryMethod")?.value === referralDeliveryMethod.GENERIC) {
                    <label class="block font-semibold text-agentium_gray-text mt-4 mb-2">API Type <span class="text-red-800">*</span> </label>
                    <div class="flex items-center mb-2">
                      <div class="flex">
                        <div class="mr-4">
                          <input
                            type="radio"
                            id="basicAuth"
                            formControlName="deliveryApiType"
                            value="{{ apiTypeEnum.BASIC }}"
                            class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 focus:ring-momentum-deep_purple"
                          />
                          <label for="basicAuth" class="ml-2 cursor-pointer text-agentium_gray-text">Basic</label>
                        </div>

                        <div class="mr-4">
                          <input
                            type="radio"
                            id="xApiKeyAuth"
                            formControlName="deliveryApiType"
                            value="{{ apiTypeEnum.X_API_KEY }}"
                            class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 focus:ring-momentum-deep_purple"
                          />
                          <label for="xApiKeyAuth" class="ml-2 cursor-pointer text-agentium_gray-text">X-API-KEY</label>
                        </div>
                      </div>
                    </div>

                    <div class="mt-2 block font-semibold text-agentium_gray-text mb-2">Generic Webhook Authorization</div>
                    <div class="flex items-center">
                      <label for="deliveryApiUrl" class="mr-7 text-agentium_gray-text">URL</label>
                      <input type="text" id="deliveryApiUrl" formControlName="deliveryApiUrl" class="mr-4 border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-[600px]" />
                    </div>
                    <!-- API Key -->
                    <div class="flex items-center mt-2">
                      <label for="deliveryApiKey" class="mr-2 text-agentium_gray-text">API Key</label>
                      <input type="text" id="deliveryApiKey" formControlName="deliveryApiKey" class="border rounded text-agentium_gray-text disabled:bg-gray-300 border-gray-300 p-2 w-[600px]" />
                    </div>
                  }
                </div>
              </div>
            }
          </div>

          <!-- Email Delivery -->
          <div class="mb-6">
            <div class="mb-6 flex">
              <label class="block font-semibold text-agentium_gray-text mr-4">Email Delivery</label>
              <div class="flex items-center">
                <input type="checkbox" formControlName="deliverToEmailAddress" id="deliverToEmailAddressToggle" class="sr-only" />
                @let deliverToEmailAddress = marketplaceForm.get("deliverToEmailAddress")?.value;
                <label
                  for="deliverToEmailAddressToggle"
                  class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer"
                  [ngClass]="{
                    'bg-momentum-deep_purple': deliverToEmailAddress,
                    'bg-gray-300': !deliverToEmailAddress
                  }"
                >
                  <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': deliverToEmailAddress }"></span>
                </label>
              </div>
            </div>

            @if (marketplaceForm.get("deliverToEmailAddress")?.value) {
              <div class="ml-2">
                <!-- Email Template -->
                <div class="mb-6">
                  <label class="block font-semibold text-agentium_gray-text mb-2">
                    Email Template <span class="text-red-800">*</span>
                  </label>
                  <div class="flex flex-wrap gap-x-8 gap-y-2">
                    @for (template of emailTemplateOptions; track template) {
                      <div class="flex items-center">
                        <input
                          type="radio"
                          [id]="'emailTemplate-' + template"
                          formControlName="emailTemplate"
                          [value]="template"
                          class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 focus:ring-momentum-deep_purple"
                        />
                        <label [for]="'emailTemplate-' + template" class="ml-2 cursor-pointer text-agentium_gray-text">
                          {{ getEmailTemplateDisplayLabel(template) }}
                        </label>
                      </div>
                    }
                  </div>
                </div>

                <!-- Round Robin -->
                <div class="flex items-center mb-4">
                  <label for="enableRoundRobinDelivery" class="mr-2 cursor-pointer font-semibold text-agentium_gray-text"> Enable Round Robin Email Delivery </label>
                  <input
                    type="checkbox"
                    id="enableRoundRobinDelivery"
                    formControlName="enableRoundRobinDelivery"
                    class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                  />
                </div>

                <!-- Email Recipients -->
                <div class="mb-6">
                  <label for="emailRecipients" class="block font-semibold text-agentium_gray-text mb-2">Referral Email Recipients <span class="text-red-800">*</span> </label>
                  <p class="text-sm text-agentium_gray-text mb-2">
                    Enter multiple email addresses seperated by a comma. (NOTE: email address(es) will appear in the Individual Recipient Delivery Setting section after saving)
                  </p>
                  <textarea
                    id="emailRecipients"
                    rows="3"
                    formControlName="emailRecipients"
                    class="border rounded p-2 text-agentium_gray-text disabled:bg-gray-300 border-gray-300 w-full h-[74px]"
                  ></textarea>
                  @if (emailRecipientsIsInvalid && marketplaceForm.get("emailRecipients")?.touched) {
                    <div class="text-red-500 mt-1">Email addresses must be valid and separated by commas.</div>
                  }
                  @if (showEmailRecipientError && marketplaceForm.get("emailRecipients")?.touched) {
                    <div class="mt-2 text-red-500">At least one email recipient is required.</div>
                  }
                  @if (showDuplicateEmailRecipientError && marketplaceForm.get("emailRecipients")?.touched) {
                    <div class="mt-2 text-red-500">Email addresses must not be duplicated.</div>
                  }
                </div>

                <!-- --------------------------- Individual Recipient Delivery Settings Section -------------------------- -->
                <div>
                  <!-- Individual Recipient Delivery Settings Toggle -->
                  @if (marketplace && marketplace.individualEmailRecipientSettings && marketplace.individualEmailRecipientSettings.length > 0 && marketplaceForm.get("emailRecipients")?.value) {
                    <div class="flex items-center mb-6">
                      <label class="block font-semibold text-agentium_gray-text mr-4">Individual Recipient Delivery Settings</label>
                      <div class="flex items-center">
                        <input
                          type="checkbox"
                          formControlName="enableIndividualEmailDeliverySettings"
                          id="enableIndividualEmailDeliverySettingsToggle"
                          class="sr-only disabled:bg-gray-300 border-gray-300"
                        />
                        @let enableIndividualEmailDeliverySettings = marketplaceForm.get("enableIndividualEmailDeliverySettings")?.value;
                        <label
                          for="enableIndividualEmailDeliverySettingsToggle"
                          class="flex items-center w-10 h-5 p-0.5 rounded-full"
                          [ngClass]="{
                            'bg-momentum-deep_purple': enableIndividualEmailDeliverySettings,
                            'bg-gray-300': !enableIndividualEmailDeliverySettings,
                            'cursor-pointer': !marketplaceForm.get('enableIndividualEmailDeliverySettings')?.disabled,
                            'cursor-not-allowed': marketplaceForm.get('enableIndividualEmailDeliverySettings')?.disabled
                          }"
                        >
                          <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': enableIndividualEmailDeliverySettings }"></span>
                        </label>
                      </div>
                    </div>

                    <!--  Individual Recipient Delivery Settings Table -->
                    @if (marketplaceForm.get("enableIndividualEmailDeliverySettings")?.value) {
                      <div formGroupName="riskAppetite">
                        <div class="w-full overflow-x-auto" formGroupName="individualEmailRecipientSettings">
                          <!-- Table header -->
                          <div class="w-full grid grid-cols-12 gap-4 mb-2">
                            <div class="font-semibold text-agentium_gray-text col-span-2">Email</div>
                            <div class="font-semibold text-agentium_gray-text col-span-1">SMS</div>
                            <div class="font-semibold text-agentium_gray-text text-center col-span-1">Match Marketplace</div>
                            <div class="font-semibold text-agentium_gray-text col-span-8">Delivery Settings</div>
                          </div>
                          <!-- Horizontal line -->
                          <div class="border-b border-gray-300 mb-4"></div>

                          <!-- Table content -->
                          @for (individualEmailRecipientSetting of marketplace.individualEmailRecipientSettings; track individualEmailRecipientSetting.id) {
                            <div [class]="'w-full grid grid-cols-12 items-start gap-4'">
                              <!-- Email column -->
                              <div class="flex items-center col-span-2">
                                <span class="text-agentium_gray-text break-words overflow-hidden min-w-0">{{ individualEmailRecipientSetting.emailAddress }}</span>
                              </div>

                              <!-- SMS column -->
                              @if (individualEmailRecipientSetting.mobilePhone) {
                                <div class="flex items-center col-span-1">
                                  @let enableSmsNotification = "enableSmsNotification-" + individualEmailRecipientSetting.id;
                                  <input
                                    type="checkbox"
                                    [id]="enableSmsNotification"
                                    [formControlName]="enableSmsNotification"
                                    class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                                  />
                                  <label class="text-agentium_gray-text break-words ml-2">{{ individualEmailRecipientSetting.mobilePhone }}</label>
                                </div>
                              } @else {
                                <div class="text-agentium_gray-text flex items-center col-span-1">n/a</div>
                              }

                              <!-- Match Marketplace Settings column -->
                              <div class="flex flex-col justify-center items-center col-span-1">
                                <!-- Match Delivery Days -->
                                <div class="flex items-center mb-6">
                                  @let sameDeliveryDaysAsMarketplace = "sameDeliveryDaysAsMarketplace-" + individualEmailRecipientSetting.id;
                                  <input
                                    type="checkbox"
                                    [id]="sameDeliveryDaysAsMarketplace"
                                    [formControlName]="sameDeliveryDaysAsMarketplace"
                                    class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                                  />
                                  <label [for]="sameDeliveryDaysAsMarketplace" class="ml-2 cursor-pointer text-agentium_gray-text text-sm">Days</label>
                                </div>
                                <!-- Match Delivery Times -->
                                <div class="flex items-center">
                                  @let sameDeliveryTimesAsMarketplace = "sameDeliveryTimesAsMarketplace-" + individualEmailRecipientSetting.id;
                                  <input
                                    type="checkbox"
                                    [id]="sameDeliveryTimesAsMarketplace"
                                    [formControlName]="sameDeliveryTimesAsMarketplace"
                                    class="ml-2 w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                                  />
                                  <label [for]="sameDeliveryTimesAsMarketplace" class="ml-2 cursor-pointer text-agentium_gray-text text-sm">Times</label>
                                </div>
                              </div>

                              <!-- Delivery Settings column -->
                              <div class="col-span-8">
                                <!-- Delivery Days -->
                                <div class="flex flex-wrap gap-8 mb-4">
                                  <!-- Monday -->
                                  <div class="flex items-center">
                                    @let acceptReferralsOnMonday = "acceptReferralsOnMonday-" + individualEmailRecipientSetting.id;
                                    <input
                                      type="checkbox"
                                      [id]="acceptReferralsOnMonday"
                                      [formControlName]="acceptReferralsOnMonday"
                                      class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                                    />
                                    <label [for]="acceptReferralsOnMonday" class="ml-2 cursor-pointer text-agentium_gray-text">Monday</label>
                                  </div>
                                  <!-- Tuesday -->
                                  <div class="flex items-center">
                                    @let acceptReferralsOnTuesday = "acceptReferralsOnTuesday-" + individualEmailRecipientSetting.id;
                                    <input
                                      type="checkbox"
                                      [id]="acceptReferralsOnTuesday"
                                      [formControlName]="acceptReferralsOnTuesday"
                                      class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                                    />
                                    <label [for]="acceptReferralsOnTuesday" class="ml-2 cursor-pointer text-agentium_gray-text">Tuesday</label>
                                  </div>
                                  <!-- Wednesday -->
                                  <div class="flex items-center">
                                    @let acceptReferralsOnWednesday = "acceptReferralsOnWednesday-" + individualEmailRecipientSetting.id;
                                    <input
                                      type="checkbox"
                                      [id]="acceptReferralsOnWednesday"
                                      [formControlName]="acceptReferralsOnWednesday"
                                      class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                                    />
                                    <label [for]="acceptReferralsOnWednesday" class="ml-2 cursor-pointer text-agentium_gray-text">Wednesday</label>
                                  </div>
                                  <!-- Thursday -->
                                  <div class="flex items-center">
                                    @let acceptReferralsOnThursday = "acceptReferralsOnThursday-" + individualEmailRecipientSetting.id;
                                    <input
                                      type="checkbox"
                                      [id]="acceptReferralsOnThursday"
                                      [formControlName]="acceptReferralsOnThursday"
                                      class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                                    />
                                    <label [for]="acceptReferralsOnThursday" class="ml-2 cursor-pointer text-agentium_gray-text">Thursday</label>
                                  </div>
                                  <!-- Friday -->
                                  <div class="flex items-center">
                                    @let acceptReferralsOnFriday = "acceptReferralsOnFriday-" + individualEmailRecipientSetting.id;
                                    <input
                                      type="checkbox"
                                      [id]="acceptReferralsOnFriday"
                                      [formControlName]="acceptReferralsOnFriday"
                                      class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                                    />
                                    <label [for]="acceptReferralsOnFriday" class="ml-2 cursor-pointer text-agentium_gray-text">Friday</label>
                                  </div>
                                  <!-- Saturday -->
                                  <div class="flex items-center">
                                    @let acceptReferralsOnSaturday = "acceptReferralsOnSaturday-" + individualEmailRecipientSetting.id;
                                    <input
                                      type="checkbox"
                                      [id]="acceptReferralsOnSaturday"
                                      [formControlName]="acceptReferralsOnSaturday"
                                      class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                                    />
                                    <label [for]="acceptReferralsOnSaturday" class="ml-2 cursor-pointer text-agentium_gray-text">Saturday</label>
                                  </div>
                                  <!-- Sunday -->
                                  <div class="flex items-center">
                                    @let acceptReferralsOnSunday = "acceptReferralsOnSunday-" + individualEmailRecipientSetting.id;
                                    <input
                                      type="checkbox"
                                      [id]="acceptReferralsOnSunday"
                                      [formControlName]="acceptReferralsOnSunday"
                                      class="w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                                    />
                                    <label [for]="acceptReferralsOnSunday" class="ml-2 cursor-pointer text-agentium_gray-text">Sunday</label>
                                  </div>
                                </div>
                                @if (hasNoDaysSelectedError(individualEmailRecipientSetting.id)) {
                                  <div class="text-red-500 text-sm mb-2">At least one delivery day must be selected.</div>
                                }

                                <!-- Delivery Times -->
                                <div [class]="'flex flex-wrap items-center gap-4 ' + ($last ? 'mb-4' : 'mb-10')">
                                  <!-- Start -->
                                  <div class="flex items-center">
                                    @let referralDeliveryStartTime = "referralDeliveryStartTime-" + individualEmailRecipientSetting.id;
                                    <label [for]="referralDeliveryStartTime" class="mr-2 cursor-pointer text-agentium_gray-text">Start</label>
                                    <input
                                      type="time"
                                      [id]="referralDeliveryStartTime"
                                      [formControlName]="referralDeliveryStartTime"
                                      class="border rounded p-2 w-full text-agentium_gray-text border-gray-300 disabled:bg-gray-300"
                                    />
                                  </div>
                                  <!-- End -->
                                  <div class="flex items-center">
                                    @let referralDeliveryEndTime = "referralDeliveryEndTime-" + individualEmailRecipientSetting.id;
                                    <label [for]="referralDeliveryEndTime" class="mr-2 cursor-pointer text-agentium_gray-text">End</label>
                                    <input
                                      type="time"
                                      [id]="referralDeliveryEndTime"
                                      [formControlName]="referralDeliveryEndTime"
                                      class="border rounded p-2 w-full text-agentium_gray-text border-gray-300 disabled:bg-gray-300"
                                    />
                                  </div>
                                  <!-- Timezone -->
                                  <div class="flex items-center">
                                    @let referralDeliveryTimezone = "referralDeliveryTimezone-" + individualEmailRecipientSetting.id;
                                    <label [for]="referralDeliveryTimezone" class="mr-2 cursor-pointer text-agentium_gray-text">Timezone</label>
                                    <select
                                      [id]="referralDeliveryTimezone"
                                      [formControlName]="referralDeliveryTimezone"
                                      class="cursor-pointer border rounded p-2 w-full text-agentium_gray-text disabled:bg-gray-300 border-gray-300"
                                    >
                                      <option value="America/Anchorage">Alaska</option>
                                      <option value="America/Chicago" selected>Central</option>
                                      <option value="America/New_York">Eastern</option>
                                      <option value="Pacific/Honolulu">Hawaii</option>
                                      <option value="America/Denver">Mountain</option>
                                      <option value="America/Los_Angeles">Pacific</option>
                                    </select>
                                  </div>
                                  <!-- 24 hrs/day -->
                                  <div class="flex items-center">
                                    @let accept24hrReferralDelivery = "accept24hrReferralDelivery-" + individualEmailRecipientSetting.id;
                                    <input
                                      type="checkbox"
                                      [id]="accept24hrReferralDelivery"
                                      [formControlName]="accept24hrReferralDelivery"
                                      class="mr-1 w-4 h-4 cursor-pointer text-momentum-deep_purple disabled:bg-gray-300 border-gray-300 rounded focus:ring-momentum-deep_purple"
                                    />
                                    <label [for]="accept24hrReferralDelivery" class="ml-2 cursor-pointer text-agentium_gray-text"> 24 hrs/day</label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          }
                        </div>
                      </div>
                    }
                  }
                </div>

                <!--Bcc Email Recipients -->
                <div class="mb-6">
                  <label for="emailBccRecipients" class="block font-semibold text-agentium_gray-text mb-2">Referral Email Bcc Recipients</label>
                  <p class="text-sm text-agentium_gray-text mb-2">Enter multiple email addresses seperated by a comma.</p>
                  <textarea
                    id="emailBccRecipients"
                    rows="3"
                    formControlName="emailBccRecipients"
                    class="border rounded p-2 text-agentium_gray-text disabled:bg-gray-300 border-gray-300 w-full h-[74px]"
                  ></textarea>
                  @if (emailBccRecipientsIsInvalid && marketplaceForm.get("emailBccRecipients")?.touched) {
                    <div class="text-red-500 mt-1">Email addresses must be valid and separated by commas.</div>
                  }
                  @if (showDuplicateEmailBccRecipientError && marketplaceForm.get("emailBccRecipients")?.touched) {
                    <div class="mt-2 text-red-500">Email addresses must not be duplicated.</div>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Form Buttons -->
      <div class="flex justify-end space-x-4">
        <button
          type="submit"
          class="px-6 py-2 bg-momentum-deep_purple enabled:text-white rounded-full flex items-center disabled:bg-agentium_gray-text disabled:cursor-not-allowed"
          [disabled]="!marketplaceForm.dirty || !isFormValid() || isSaving"
        >
          Save Marketplace
          @if (isSaving) {
            <fa-icon [icon]="faGear" animation="spin" class="pl-4 text-slate-600" size="lg"></fa-icon>
          }
        </button>
        <button
          type="button"
          class="px-6 py-2 bg-agentium_white-backdrop border border-gray-300 rounded-full disabled:bg-agentium_gray-text disabled:cursor-not-allowed"
          [disabled]="!marketplaceForm.dirty || isSaving"
          (click)="discardChanges()"
        >
          Discard Changes
        </button>
      </div>
    </form>
  }

  <!-- Page Loading Spinner -->
  @if (isLoading) {
    <div class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70">
      <fa-icon [icon]="faGear" animation="spin" class="p-4 text-slate-600" size="2xl"></fa-icon>
    </div>
  }
</div>
